import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { resendVerification } from '../../services/api';

const Container = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  text-align: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #4a90e2;
  margin-bottom: 1.5rem;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ResendButton = styled.button`
  padding: 0.6rem 1.25rem;
  border-radius: 4px;
  border: none;
  background-color: #4a90e2;
  color: #fff;
  cursor: pointer;
  font-size: 0.95rem;
  margin-right: 0.75rem;

  &:disabled {
    background-color: #a0c4f7;
    cursor: not-allowed;
  }
`;

const BackToLoginLink = styled(Link)`
  color: #4a90e2;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const VerifyAccount = () => {
  const location = useLocation();
  const registeredEmail = location.state?.email;
  const [resendStatus, setResendStatus] = useState(null); // 'success' | 'error' | null
  const [resendMessage, setResendMessage] = useState('');
  const [cooldown, setCooldown] = useState(30); // seconds until resend is allowed

  useEffect(() => {
    if (!registeredEmail) return;
    if (cooldown <= 0) return;

    const timer = setTimeout(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldown, registeredEmail]);

  const handleResend = async () => {
    if (!registeredEmail) return;
    if (cooldown > 0) return;
    try {
      setResendStatus(null);
      setResendMessage('');
      const response = await resendVerification(registeredEmail);
      setResendStatus('success');
      setResendMessage(response.data?.message || 'Verification email resent.');
      setCooldown(30);
    } catch (error) {
      setResendStatus('error');
      const message =
        error.response?.data?.message ||
        'Could not resend verification email. Please try again later.';
      setResendMessage(message);
    }
  };

  return (
    <Container>
      <Title>Verify Your Email</Title>
      <Message>
        {registeredEmail ? (
          <>
            For your security, please verify your email address before accessing your dashboard.
            Weve sent a verification email to <strong>{registeredEmail}</strong>. If you dont see it,
            please check your Junk or Spam folder.
          </>
        ) : (
          <>
            For your security, please verify your email address before accessing your dashboard.
            If you no longer have the verification email, you can request a new one from the
            registration page.
          </>
        )}
      </Message>
      {registeredEmail && (
        <div style={{ marginBottom: '1rem' }}>
          <ResendButton type="button" onClick={handleResend} disabled={cooldown > 0}>
            {cooldown > 0
              ? `Resend available in ${cooldown}s`
              : 'Resend verification email'}
          </ResendButton>
          <BackToLoginLink to="/login">Back to login</BackToLoginLink>
        </div>
      )}
      {resendStatus && (
        <p style={{ color: resendStatus === 'success' ? 'green' : 'red' }}>{resendMessage}</p>
      )}
    </Container>
  );
};

export default VerifyAccount;
